import React, { useState } from "react";
import { P_Props } from "./FaqForm.type";
import { Form, Field } from "react-final-form";
import { FormUtil, FormUIUtil, loadCallback, ImageUtil } from "../../../utils";
import { Loading, SimpleModalHeader, Notification, Pulse, CategoryCard1 } from "../../components";
import { TextField } from "@material-ui/core";
import { PriorityHigh as PriorityHighIcon } from "@material-ui/icons";
import { FORM_MODE } from "../../../models";

const FaqFormPresenter: React.FC<P_Props> = (props) => {
  let submit: any;
  let globalDirty: any;
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [visibleSaveWarning, setVisibleSaveWarning] = React.useState<{
    visible: boolean;
  }>({ visible: false });

  const toggleDeleteModal = () => {
    setVisibleDeleteModal(!visibleDeleteModal);
  };

  const toggleSaveWarningModal = (visible: boolean, isChanged?: boolean) => {
    setVisibleSaveWarning({ visible: visible });
    globalDirty = isChanged;
  };

  const bindingFaqDataToForm = (data: any[]) => {
    return FormUtil.buildInitDataForm(data, [{ key: "question" }, { key: "answer" }]);
  };

  return (
    <>
      <div className="faq-form">
        <SimpleModalHeader
          title={props.mode === FORM_MODE.CREATE ? "New FAQ" : "Edit FAQ"}
          onClose={() => {
            props.onCancel();
          }}
        />

        <div className="faq-form__divide">
          <div className="faq-form__language">
            <h1>Language</h1>

            {props.languageOptions.map((lang) => {
              return (
                <CategoryCard1
                  key={lang.id}
                  name={lang.name}
                  onClick={() => {
                    props.onChangeLanguage(lang.id);
                  }}
                  picture={lang.image}
                  selected={props.activeLang === lang.id}
                  buildPictureSrc={ImageUtil.buildLanguageImgSrc}
                  isAvatar={true}
                />
              );
            })}
          </div>
          <div className="faq-form__container">
            <div className="faq-form__box">
              {props.loading && <Loading />}
              {!props.loading && (
                <Form
                  initialValues={props.initData ? bindingFaqDataToForm(props.initData) : {}}
                  initialValuesEqual={() => true}
                  onSubmit={async (values) => {
                    props.onSubmit(values);
                  }}
                  validate={() => {
                    const errors: any = {};
                    return errors;
                  }}
                  render={({ handleSubmit, pristine, errors, dirty }) => {
                    if (dirty && !visibleSaveWarning.visible && props.mode === FORM_MODE.EDIT) {
                      globalDirty = dirty;
                    }
                    submit = handleSubmit;

                    return (
                      <form onSubmit={handleSubmit}>
                        {props.languageOptions.map((lang) => {
                          return (
                            <div
                              key={lang.id}
                              style={{
                                display: props.activeLang === lang.id ? "block" : "none",
                              }}
                            >
                              <Field
                                name={`question${lang.id}`}
                                validate={lang.isPrimary ? FormUtil.Rule.required("Please input question") : undefined}
                                subscription={{
                                  touched: true,
                                  error: true,
                                  value: true,
                                }}
                              >
                                {({ input, meta, ...rest }) => {
                                  return FormUIUtil.renderFormItem(
                                    `Question (${lang.name})`,
                                    <TextField
                                      {...input}
                                      {...rest}
                                      fullWidth
                                      placeholder="Add question"
                                      className="form-text-field"
                                      inputProps={{ className: "input" }}
                                      variant="outlined"
                                      onChange={(e) => input.onChange(e.target.value)}
                                      helperText={meta.touched ? meta.error : ""}
                                      error={meta.error && meta.touched}
                                    />,
                                    false,
                                    true,
                                  );
                                }}
                              </Field>

                              <Field
                                name={`answer${lang.id}`}
                                validate={lang.isPrimary ? FormUtil.Rule.required("Please input answer") : undefined}
                                subscription={{
                                  touched: true,
                                  error: true,
                                  value: true,
                                }}
                              >
                                {({ input, meta, ...rest }) => {
                                  return FormUIUtil.renderFormItem(
                                    `Answer (${lang.name})`,
                                    <TextField
                                      {...input}
                                      {...rest}
                                      fullWidth
                                      multiline
                                      rows={6}
                                      placeholder="Add answer"
                                      className="form-textarea-field"
                                      inputProps={{ className: "input" }}
                                      variant="outlined"
                                      onChange={(e) => input.onChange(e.target.value)}
                                      helperText={meta.touched ? meta.error : ""}
                                      error={meta.error && meta.touched}
                                    />,
                                    false,
                                    true,
                                  );
                                }}
                              </Field>
                            </div>
                          );
                        })}
                      </form>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="faq-form__footer">
          {props.mode !== FORM_MODE.CREATE && (
            <button
              className="btn delete"
              onClick={(e) => {
                e.preventDefault();
                toggleDeleteModal();
              }}
            >
              Delete
            </button>
          )}
          <button
            className="btn cancel"
            onClick={(e) => {
              e.preventDefault();
              globalDirty && props.mode === FORM_MODE.EDIT
                ? toggleSaveWarningModal(true, globalDirty)
                : props.onCancel();
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn save"
            onClick={(e) => {
              loadCallback(submit, e);
            }}
          >
            Save
          </button>
        </div>
      </div>

      <Notification
        visible={visibleDeleteModal}
        title="Delete FAQ"
        subTitle="Are you sure you want to delete this FAQ?"
        icon={
          <Pulse color={"red"}>
            <div className="notification-icon-box red">
              <PriorityHighIcon style={{ fontSize: "5rem", color: "#fff" }} />
            </div>
          </Pulse>
        }
        actions={
          <>
            <button
              className="faq-form__delete-modal__actions__cancel"
              onClick={() => {
                toggleDeleteModal();
              }}
            >
              Cancel
            </button>
            <button
              className="faq-form__delete-modal__actions__confirm"
              onClick={() => {
                toggleDeleteModal();
                props.onDeleteFaq();
              }}
            >
              Confirm
            </button>
          </>
        }
      />

      <Notification
        visible={visibleSaveWarning.visible}
        title={"Warning Leave"}
        subTitle={`You haven't saved your changes. Are you sure you want leave?`}
        icon={
          <Pulse color={"red"}>
            <div className="notification-icon-box red">
              <PriorityHighIcon style={{ fontSize: "5rem", color: "#fff" }} />
            </div>
          </Pulse>
        }
        actions={
          <>
            <button
              className="faq-form__delete-modal__actions__cancel"
              onClick={() => {
                toggleSaveWarningModal(false, true);
              }}
            >
              Cancel
            </button>
            <button
              className="faq-form__delete-modal__actions__confirm"
              onClick={() => {
                toggleSaveWarningModal(false);
                props.onCancel();
              }}
            >
              Confirm
            </button>
          </>
        }
      />
    </>
  );
};

export default FaqFormPresenter;
